import axios from 'axios';
import auth from '../Auth/Auth';  // Import the auth utility

import BASE_URL from '../config' ;

// API base URL
const API_BASE_URL = BASE_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = auth.getToken();
  if (!token) return {};
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL
});

// Add request interceptor to attach authentication token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Add authorization header with JWT token if available
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    
    // Handle token expiration
    if (response && response.status === 401) {
      // Redirect to login or refresh token
      auth.logout();
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

const Api = {
  // Fetch all transactions
  getTransactions: async () => {
    try {
      const response = await axiosInstance.get('/transactions/');
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },

  // Get tenant information by phone number
  getTenantByPhone: async (phoneNumber) => {
    try {
      const response = await axiosInstance.get(`/tenants/phone/${phoneNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant by phone:', error);
      throw error;
    }
  },

  getTenantSelection: async () => {
    try {
      const response = await axiosInstance.get('/tenants/');
      // TenantListView returns a plain array (no pagination)
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      console.error('Error fetching tenants for selection:', error);
      throw error;
    }
  },

  // Confirm a single transaction (assign to one tenant)
  confirmTransaction: async (transactionId, tenantId) => {
    try {
      const response = await axiosInstance.post('/transactions/confirm/', {
        transaction_id: transactionId,
        tenant_id: tenantId
      });
      return response.data;
    } catch (error) {
      console.error('Error confirming transaction:', error);
      throw error;
    }
  },

  // Distribute a transaction among multiple tenants
  distributeTransaction: async (transactionId, allocations) => {
    try {
      const response = await axiosInstance.post('/transactions/distribute/', {
        transaction_id: transactionId,
        allocations: allocations
      });
      return response.data;
    } catch (error) {
      console.error('Error distributing transaction:', error);
      throw error;
    }
  },

  // Generate receipt for a transaction
  generateReceipt: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/generate-receipt/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error generating receipt:', error);
      throw error;
    }
  },

  // Edit date/reference of a manually recorded payment
  editManualPayment: async (transactionId, paymentDatetime, transactionReference) => {
    try {
      const response = await axiosInstance.patch('/manual-payment-edit/', {
        transaction_id: transactionId,
        ...(paymentDatetime      && { payment_datetime:      paymentDatetime }),
        ...(transactionReference && { transaction_reference: transactionReference }),
      });
      return response.data;
    } catch (error) {
      console.error('Error editing manual payment:', error);
      throw error;
    }
  },

  // Create manual payment record
  recordManualPayment: async (paymentData) => {
    try {
      const response = await axiosInstance.post('/manual-payment-record/', paymentData);
      return response.data;
    } catch (error) {
      console.error('Error recording manual payment:', error);
      throw error;
    }
  },

  // Edit date/reference of a manual payment
  editManualPayment: async (transactionId, paymentDatetime, transactionReference) => {
    try {
      const response = await axiosInstance.patch('/manual-payment-edit/', {
        transaction_id: transactionId,
        ...(paymentDatetime      && { payment_datetime:      paymentDatetime }),
        ...(transactionReference && { transaction_reference: transactionReference }),
      });
      return response.data;
    } catch (error) {
      console.error('Error editing manual payment:', error);
      throw error;
    }
  },

  // Get transaction details by ID
  getTransactionById: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/transaction/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      throw error;
    }
  },

  // Get transaction history for a tenant
  getTenantTransactions: async (tenantId) => {
    try {
      const response = await axiosInstance.get(`/tenant-transactions/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant transactions:', error);
      throw error;
    }
  },

  // Filter transactions by date range, status, etc.
  filterTransactions: async (filters) => {
    try {
      const response = await axiosInstance.post('/filter-transactions/', filters);
      return response.data;
    } catch (error) {
      console.error('Error filtering transactions:', error);
      throw error;
    }
  },

  // NEW EXPORT METHODS
  // Export transactions with filters
  exportTransactions: async (filters) => {
    try {
      const response = await axiosInstance.post('/transactions/export/', filters, {
        responseType: 'blob' // Important for CSV downloads
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting transactions:', error);
      throw error;
    }
  },

  // Export transactions by phone number
  exportTransactionsByPhone: async (phoneNumber) => {
    try {
      const response = await axiosInstance.post('/transactions/export/', {
        filter_type: 'phone',
        phone_number: phoneNumber
      }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting transactions by phone:', error);
      throw error;
    }
  },

  // Export transactions by customer name
  exportTransactionsByName: async (customerName) => {
    try {
      const response = await axiosInstance.post('/transactions/export/', {
        filter_type: 'name',
        customer_name: customerName
      }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting transactions by name:', error);
      throw error;
    }
  },

  // Export transactions by date range
  exportTransactionsByDateRange: async (startDate, endDate) => {
    try {
      const response = await axiosInstance.post('/transactions/export/', {
        filter_type: 'date_range',
        start_date: startDate,
        end_date: endDate
      }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting transactions by date range:', error);
      throw error;
    }
  }
};

export default Api;